import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { createItineraryReport, createItineraryReportFinished } from '@capital-access/common/actions';
import { getFeatureToggle } from '@capital-access/common/feature-toggle';
import {
  getUserSettingsCulture,
  getUserSettingsCurrency,
  getUserSettingsLanguage,
  getUserSettingsTimezone
} from '@capital-access/common/globalization';
import { getSelectedSecurity, getSelectedSecuritySurveillancePermission } from '@capital-access/common/security';
import { DEFAULT_TEMPLATE_KEY } from '@capital-access/common/utils';
import {
  createConfigurableItineraryReport,
  ReportType,
  TemplateLibraryService
} from '@capital-access/reporting/common';
import { generateReport } from '../../core/+state/report.actions';
import { OwnershipSource } from '../../core/models/ownership-source.enum';
import { ReportSection } from '../../core/models/report-sections';
import { FileNameService } from '../../core/services/file-name.service';
import { getValidItineraryLayout } from '../../core/utils/configurable-template.util';
import { getSavedSelectedSectionsFromGroups } from '../../core/utils/section-settings.utils';
import { SETTINGS_KEY } from '../constants';
import { CreateItineraryReportRequest } from '../models/itinerary-report.models';
import { getDefaultSections, ItineraryReportLayout } from '../models/itinerary-report-sections';
import { LocalizationKeys } from '../models/localization.model';
import { createItineraryCustomReport } from './itinerary-report.actions';

@Injectable()
export class ItineraryReportEffects {
  // For quick-create from workflow components (schedule, events-table), check if
  // the user has set Configurable as the default. If so, dispatch createConfigurableItineraryReport
  // with the saved default template. Otherwise, fall through to the standard report flow.
  createItineraryReportRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createItineraryReport),
      switchMap(action =>
        getValidItineraryLayout(this.store, this.templateService).pipe(map(({ layout }) => ({ action, layout })))
      ),
      mergeMap(({ action, layout }) => {
        if (layout === ItineraryReportLayout.Configurable && !action.isConfigurableDisabled) {
          const templateId = Number(
            localStorage.getItem(`${ReportType.Itinerary}_${DEFAULT_TEMPLATE_KEY}`.toUpperCase()) ?? 0
          );
          if (templateId > 0) {
            return [
              createConfigurableItineraryReport({
                eventId: action.eventId,
                templateId,
                eventName: action.eventName
              })
            ];
          }
        }
        // Standard report flow
        return of(action).pipe(this.createItineraryReport());
      })
    )
  );

  createItineraryCustomReportRequest$ = createEffect(() =>
    this.actions$.pipe(ofType(createItineraryCustomReport), this.createItineraryReport())
  );

  private createItineraryReport() {
    return (
      source: Observable<{
        eventId: string;
        eventName: string;
        sections?: ReportSection[];
      }>
    ) =>
      source.pipe(
        withLatestFrom(
          this.store.select(getSelectedSecurity),
          this.store.select(getUserSettingsTimezone),
          this.store.select(getSelectedSecuritySurveillancePermission),
          this.store.select(getUserSettingsCurrency),
          this.store.select(getFeatureToggle('skyl-2231-culture-specific-formatting')),
          this.store.select(getUserSettingsCulture),
          this.store.select(getUserSettingsLanguage)
        ),
        mergeMap(([source, security, timeZone, isSrvPermissioned, currency, cultureFtEnabled, culture, language]) => {
          const isSrv = security?.surveillance ?? false;
          return this.fileNameService.getDefaultFileName(isSrvPermissioned, isSrv, source.eventName, timeZone).pipe(
            map(fileName => {
              const requestedSections = source.sections;
              const sections = requestedSections
                ? requestedSections
                : getSavedSelectedSectionsFromGroups(SETTINGS_KEY, getDefaultSections());
              const createItineraryReportRequest: CreateItineraryReportRequest = {
                eventId: source.eventId,
                timeZone,
                securityId: security?.id,
                fileName,
                currency,
                culture: cultureFtEnabled ? culture : 'en-US',
                language: language,
                dataSource: isSrv ? OwnershipSource.Surveillance : OwnershipSource.Public,
                sections
              };

              return generateReport({
                request: createItineraryReportRequest,
                url: 'itinerary-reports/process/launch',
                successMessage: LocalizationKeys.ItineraryReportCreated,
                failureMessage: LocalizationKeys.ItineraryReportFailed,
                finishAction: createItineraryReportFinished({ eventId: source.eventId })
              });
            })
          );
        })
      );
  }

  constructor(
    private actions$: Actions,
    private store: Store,
    private fileNameService: FileNameService,
    private templateService: TemplateLibraryService
  ) {}
}
